import type { ScheduledPostDto } from "@nlc/shared-types";

import { getLocalTimeZone, toCalendarDateTime } from "@internationalized/date";
import { formatHasCaption } from "@nlc/shared-types";
import { ChevronLeft } from "lucide-react";
import { useState } from "react";

import { AccessibleIcon } from "@/components/accessible-icon";
import { Button } from "@/components/button";
import { Step, Stepper, useStepper } from "@/components/stepper";
import { toast } from "@/components/toast/toast-queue";

import type { DesignFile } from "./design-step/types";
import type { PostType } from "./post-type-step/postTypes";

import { useCreateScheduledPost } from "../../hooks/useCreateScheduledPost";
import { useSocialPages } from "../../hooks/useSocialPages";
import { useUpdateScheduledPost } from "../../hooks/useUpdateScheduledPost";
import { BrandStep } from "./BrandStep";
import { designsProblem } from "./design-step/designProblem";
import { DesignStep } from "./design-step/DesignStep";
import { PostTypeStep } from "./post-type-step/PostTypeStep";
import { designFilesOf, postTypeOf, scheduleDraftOf } from "./postToEdit";
import {
  captionMaxLength,
  captionText,
  emptyScheduleDraft,
} from "./scheduleDraft";
import { ScheduleStep } from "./ScheduleStep";
import { WizardProgress } from "./WizardProgress";

const STEPS = ["Brand", "Post type", "Designs", "Schedule"];

// Editing opens on the Schedule step: brand, format and designs are fixed once scheduled.
export function ScheduleWizard({
  onClose,
  onGoToSettings,
  postToEdit,
}: {
  onClose: () => void;
  onGoToSettings: () => void;
  postToEdit?: ScheduledPostDto;
}) {
  return (
    <Stepper
      className="flex flex-col rounded-xl border border-border-secondary bg-bg-primary"
      initialStep={postToEdit ? STEPS.length : 1}
      totalSteps={STEPS.length}
    >
      <ScheduleWizardContent
        onClose={onClose}
        onGoToSettings={onGoToSettings}
        postToEdit={postToEdit}
      />
    </Stepper>
  );
}

function ScheduleWizardContent({
  onClose,
  onGoToSettings,
  postToEdit,
}: {
  onClose: () => void;
  onGoToSettings: () => void;
  postToEdit?: ScheduledPostDto;
}) {
  const { goToStep, nextStep, prevStep, step } = useStepper();
  const [brandId, setBrandId] = useState(postToEdit?.brand.id);
  const [postType, setPostType] = useState<PostType | undefined>(
    postToEdit && postTypeOf(postToEdit),
  );
  const [designs, setDesigns] = useState<DesignFile[]>(
    postToEdit ? designFilesOf(postToEdit) : [],
  );
  const [schedule, setSchedule] = useState(() =>
    postToEdit ? scheduleDraftOf(postToEdit) : emptyScheduleDraft(),
  );
  const { data: pagesData } = useSocialPages();
  const createScheduledPost = useCreateScheduledPost();
  const updateScheduledPost = useUpdateScheduledPost();
  const isEditing = postToEdit !== undefined;
  const closesWizard = step === 1 || isEditing;
  const caption = captionText(schedule.caption);
  const postNow = schedule.goLive === "now";
  const scheduledAt =
    !postNow && schedule.date && schedule.time
      ? toCalendarDateTime(schedule.date, schedule.time).toDate(
          getLocalTimeZone(),
        )
      : undefined;
  const page = pagesData?.pages.find(
    (page) => page.brand.id === brandId && page.platform === postType?.platform,
  );
  const missingChoice = [
    !brandId && "Choose a brand to continue",
    !postType && "Choose a post type to continue",
    postType && designsProblem(designs, postType),
    scheduleProblem(),
  ][step - 1];

  function scheduleProblem() {
    if (!schedule.name.trim()) return "Name your post";
    if (!postNow) {
      if (!scheduledAt) return "Pick a date and time";
      if (scheduledAt.getTime() <= Date.now()) {
        return "Pick a time in the future";
      }
    }
    if (!postType || !formatHasCaption(postType.format)) return;
    if (!caption) return "Write or generate a caption";
    if (caption.length > captionMaxLength(postType.platform)) {
      return "Shorten your caption";
    }
  }

  function savePost() {
    if (postToEdit) {
      updatePost(postToEdit.id);
    } else {
      schedulePost();
    }
  }

  function updatePost(id: number) {
    if (!postType || (!postNow && !scheduledAt)) return;

    updateScheduledPost.mutate(
      {
        body: {
          caption: formatHasCaption(postType.format) ? caption : null,
          name: schedule.name.trim(),
          scheduledAt: scheduledAt?.toISOString() ?? null,
        },
        id,
      },
      {
        onError: (error) => {
          toast.add(
            {
              title: error.message ?? "Could not update the schedule",
              type: "error",
            },
            { timeout: 5000 },
          );
        },
        onSuccess: () => {
          toast.add(
            {
              title: postNow ? "Your post is publishing" : "Schedule updated",
              type: "success",
            },
            { timeout: 5000 },
          );
          onClose();
        },
      },
    );
  }

  function schedulePost() {
    if (
      designs.length === 0 ||
      !page ||
      !postType ||
      (!postNow && !scheduledAt)
    ) {
      return;
    }

    createScheduledPost.mutate(
      {
        attachmentIds: designs.map((design) => Number(design.id)),
        caption: formatHasCaption(postType.format) ? caption : null,
        format: postType.format,
        name: schedule.name.trim(),
        scheduledAt: scheduledAt?.toISOString() ?? null,
        socialPageId: page.id,
      },
      {
        onError: (error) => {
          toast.add(
            {
              title:
                error.message ??
                (postNow
                  ? "Could not publish the post"
                  : "Could not schedule the post"),
              type: "error",
            },
            { timeout: 5000 },
          );
        },
        onSuccess: () => {
          toast.add(
            {
              title: postNow ? "Your post is publishing" : "Post scheduled",
              type: "success",
            },
            { timeout: 5000 },
          );
          onClose();
        },
      },
    );
  }

  function selectBrand(id: number) {
    setBrandId(id);
    setPostType(undefined);
    setDesigns([]);
    setSchedule(emptyScheduleDraft());
  }

  return (
    <>
      <header className="flex items-center gap-xl border-b border-border-secondary p-xl">
        <Button
          aria-label={closesWizard ? "Close" : "Previous step"}
          color="gray"
          isIconOnly
          onPress={closesWizard ? onClose : prevStep}
          variant="secondary"
        >
          <AccessibleIcon>
            <ChevronLeft />
          </AccessibleIcon>
        </Button>
        <h1 className="text-2xl font-semibold text-text-primary">
          {isEditing ? "Edit schedule" : "Schedule a post"}
        </h1>
      </header>

      {!isEditing && (
        <WizardProgress onGoToStep={goToStep} step={step} steps={STEPS} />
      )}

      <div className="w-full p-3xl">
        <Step className="mx-auto flex w-full max-w-[1760px] flex-col" id={1}>
          <BrandStep
            onGoToSettings={onGoToSettings}
            onSelectBrand={selectBrand}
            selectedBrandId={brandId}
          />
        </Step>
        <Step className="flex w-full flex-col" id={2}>
          {brandId && (
            <PostTypeStep
              brandId={brandId}
              onSelectPostType={setPostType}
              selectedPostType={postType}
            />
          )}
        </Step>
        <Step className="flex w-full flex-col" id={3}>
          {brandId && postType && (
            <DesignStep
              brandId={brandId}
              key={brandId}
              onSelectDesigns={setDesigns}
              postType={postType}
              selectedDesigns={designs}
            />
          )}
        </Step>
        <Step className="flex w-full flex-col" id={4}>
          {brandId && postType && designs.length > 0 && (
            <ScheduleStep
              brandId={brandId}
              designs={designs}
              draft={schedule}
              onChange={setSchedule}
              onChangeFormat={isEditing ? undefined : () => goToStep(2)}
              postType={postType}
            />
          )}
        </Step>
      </div>

      <footer className="sticky bottom-0 z-10 flex items-center justify-between gap-lg rounded-b-xl border-t border-border-secondary bg-bg-primary p-xl shadow-[0_-4px_12px_-4px_rgb(16_24_40/0.08)]">
        <Button
          color="gray"
          onPress={closesWizard ? onClose : prevStep}
          variant="secondary"
        >
          {closesWizard ? "Cancel" : "Back"}
        </Button>
        <div className="flex items-center gap-lg">
          {missingChoice && (
            <span className="text-sm text-text-tertiary" role="status">
              {missingChoice}
            </span>
          )}
          {step === STEPS.length ? (
            <Button
              isDisabled={Boolean(missingChoice)}
              isLoading={
                createScheduledPost.isPending || updateScheduledPost.isPending
              }
              onPress={savePost}
            >
              {postNow
                ? "Post now"
                : isEditing
                  ? "Save changes"
                  : "Schedule post"}
            </Button>
          ) : (
            <Button isDisabled={Boolean(missingChoice)} onPress={nextStep}>
              Continue
            </Button>
          )}
        </div>
      </footer>
    </>
  );
}
